import React from "react";
import axios from "axios";
import Pagination from "../../Pagination/Pagination";
import { API_URL } from "../../config";
import Loader from "../Loader/Loader";
import { useAppContext } from "../../Lib/ContextLib";
import html2pdf from "html2pdf.js";
import moment from "moment";
import Select from "react-select";
import EmptyState from "../../Components/EmptyState";

const filterSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: "10px",
    border: state.isFocused ? "1.5px solid #ffc107" : "1.5px solid #e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(255, 193, 7, 0.15)" : "0 1px 2px rgba(0, 0, 0, 0.03)",
    padding: "2px 8px",
    fontSize: "0.95rem",
    backgroundColor: "#ffffff",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: state.isFocused ? "#ffc107" : "#cbd5e1",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? "#ffc107" 
      : state.isFocused 
      ? "rgba(255, 193, 7, 0.1)" 
      : "#fff",
    color: state.isSelected ? "#000" : "#1e293b",
    padding: "10px 15px",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#ffc107",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: "12px",
    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
    border: "1px solid #e2e8f0",
    zIndex: 9999,
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
};

export default function History() {
  const { accessObject } = useAppContext();
  const [dataRows, setdataRows] = React.useState([]);
  const [searchPhoneNo, setSearchPhoneNo] = React.useState("");
  const [showPrePage, setShowPrePage] = React.useState(10);
  const [loader, setLoader] = React.useState(false);
  const [pagination, setPagination] = React.useState({
    start: 0,
    end: showPrePage,
  });
  const [pageNumber, setPageNumber] = React.useState(1);
  const [recordLength, setRecordLength] = React.useState(10);
  const [totalLength, setTotalLength] = React.useState(0);
  const [filters, setFilters] = React.useState({
    startDate: "",
    endDate: "",
    playerId: "",
    gameNo: -1,
    transactionType: -1,
    note: -1,
    status: -1,
    withdrawType: -1,
    walletType: -1,
  });

  React.useEffect(() => {
    getBranch();
  }, [searchPhoneNo, pageNumber, recordLength, filters]);

  const onPaginationChange = (start, end) => {
    setPagination({ start: start, end: end });
  };
  const updateShowPerpagevalue = (value) => {
    setShowPrePage(value);
    setPagination({ start: 0, end: value });
  };

  const getBranch = () => {
    setLoader(true);

    axios
      .post(`${API_URL}/api/admin/history/get-history-data`, {
        pageNumber: pageNumber,
        recordLength: recordLength,
        search: searchPhoneNo,
        startDate: filters.startDate,
        endDate: filters.endDate,
        playerId: filters.playerId,
        gameNo: filters.gameNo,
        transactionType: filters.transactionType,
        withdrawType: filters.withdrawType,
        note: filters.note,
        status: filters.status,
        walletType: filters.walletType,
      })
      .then((response) => {
        setLoader(false);
        setdataRows(response?.data?.data?.getHistoryData || []);
        setTotalLength(response?.data?.data?.totalLength || 0);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPageNumber(1); // Reset to first page when filters change
  };

  const exportHistoryData = async () => {
    try {
      setLoader(true);
      const response = await axios.post(
        `${API_URL}/api/admin/report/download-history`,
        {
          // Include all the current filter parameters
          startDate: filters.startDate,
          endDate: filters.endDate,
          playerId: filters.playerId,
          gameNo: filters.gameNo,
          transactionType: filters.transactionType,
          withdrawType: filters.withdrawType,
          note: filters.note,
          status: filters.status,
          walletType: filters.walletType,
        },
        {
          responseType: "blob",
        }
      );

      // Check if we got a valid response
      if (!response.data || response.data.size === 0) {
        throw new Error("Empty response from server");
      }

      // Determine file type from content type
      const contentType = response.headers["content-type"];
      const fileExtension = contentType.includes("excel")
        ? "xlsx"
        : contentType.includes("csv")
        ? "csv"
        : "xlsx"; // default to xlsx

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `history_${moment().format("YYYY-MM-DD")}.${fileExtension}`
      );
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        link.remove();
      }, 100);
    } catch (error) {
      console.error("Export error:", error.response || error);
    } finally {
      setLoader(false);
    }
  };

  const renderFilters = () => (
    <div className="card-header bg-white border-bottom-0 pb-0 pt-4 px-4">
      <div className="row align-items-end justify-content-start">
        {/* Date Range */}
        <div className="col-md-2 mb-3">
          <label className="small text-muted font-weight-bold mb-1">Start Date</label>
          <input
            type="date"
            className="form-control"
            style={{ borderRadius: "10px", border: "1.5px solid #e2e8f0", padding: "8px 12px", fontSize: "0.95rem" }}
            value={filters.startDate}
            onChange={(e) => handleFilterChange("startDate", e.target.value)}
          />
        </div>

        <div className="col-md-2 mb-3">
          <label className="small text-muted font-weight-bold mb-1">End Date</label>
          <input
            type="date"
            className="form-control"
            style={{ borderRadius: "10px", border: "1.5px solid #e2e8f0", padding: "8px 12px", fontSize: "0.95rem" }}
            value={filters.endDate}
            onChange={(e) => handleFilterChange("endDate", e.target.value)}
          />
        </div>

        {/* Game Type */}
        <div className="col-md-2 mb-3">
          <label className="small text-muted font-weight-bold mb-1">Game</label>
          <Select
            styles={filterSelectStyles}
            options={[
              { value: -1, label: "All" },
              { value: 1, label: "Smash Block" },
              { value: 2, label: "Tic Tac Toe" },
              { value: 3, label: "Block Blast" },
              { value: 4, label: "Word Book" },
              { value: 5, label: "Number Puzzle 2248" },
              { value: 6, label: "Logic Line" },
              { value: 7, label: "Candy Cream" },
              { value: 8, label: "8 ball Pool" },
              { value: 9, label: "Chess Gamebit" },
              { value: 10, label: "Ludo Lion" },
              { value: 11, label: "Poker Pro" },
              { value: 12, label: "Classy Carrom" },
              { value: 13, label: "Teen Patti" },
            ]}
            value={
              [
                { value: -1, label: "All" },
                { value: 1, label: "Smash Block" },
                { value: 2, label: "Tic Tac Toe" },
                { value: 3, label: "Block Blast" },
                { value: 4, label: "Word Book" },
                { value: 5, label: "Number Puzzle 2248" },
                { value: 6, label: "Logic Line" },
                { value: 7, label: "Candy Cream" },
                { value: 8, label: "8 ball Pool" },
                { value: 9, label: "Chess Gamebit" },
                { value: 10, label: "Ludo Lion" },
                { value: 11, label: "Poker Pro" },
                { value: 12, label: "Classy Carrom" },
                { value: 13, label: "Teen Patti" },
              ].find((o) => o.value === filters.gameNo) || { value: -1, label: "All" }
            }
            onChange={(option) => handleFilterChange("gameNo", option.value)}
          />
        </div>

        {/* Transaction Type */}
        <div className="col-md-2 mb-3">
          <label className="small text-muted font-weight-bold mb-1">Transaction</label>
          <Select
            styles={filterSelectStyles}
            options={[
              { value: -1, label: "All" },
              { value: 0, label: "Debit" },
              { value: 1, label: "Credit" },
            ]}
            value={
              [
                { value: -1, label: "All" },
                { value: 0, label: "Debit" },
                { value: 1, label: "Credit" },
              ].find((o) => o.value === filters.transactionType) || { value: -1, label: "All" }
            }
            onChange={(option) => handleFilterChange("transactionType", option.value)}
          />
        </div>

        {/* Note Type */}
        <div className="col-md-2 mb-3">
          <label className="small text-muted font-weight-bold mb-1">Note</label>
          <Select
            styles={filterSelectStyles}
            options={[
              { value: -1, label: "All" },
              { value: 0, label: "Win" },
              { value: 1, label: "Bet" },
              { value: 2, label: "Add Cash" },
              { value: 3, label: "Withdraw" },
              { value: 4, label: "Reward" },
              { value: 5, label: "Refund" },
              { value: 6, label: "Test" },
            ]}
            value={
              [
                { value: -1, label: "All" },
                { value: 0, label: "Win" },
                { value: 1, label: "Bet" },
                { value: 2, label: "Add Cash" },
                { value: 3, label: "Withdraw" },
                { value: 4, label: "Reward" },
                { value: 5, label: "Refund" },
                { value: 6, label: "Test" },
              ].find((o) => o.value === filters.note) || { value: -1, label: "All" }
            }
            onChange={(option) => handleFilterChange("note", option.value)}
          />
        </div>

        {/* Status */}
        <div className="col-md-2 mb-3">
          <label className="small text-muted font-weight-bold mb-1">Status</label>
          <Select
            styles={filterSelectStyles}
            options={[
              { value: -1, label: "All" },
              { value: 0, label: "Pending" },
              { value: 1, label: "Success" },
              { value: 2, label: "Failed" },
            ]}
            value={
              [
                { value: -1, label: "All" },
                { value: 0, label: "Pending" },
                { value: 1, label: "Success" },
                { value: 2, label: "Failed" },
              ].find((o) => o.value === filters.status) || { value: -1, label: "All" }
            }
            onChange={(option) => handleFilterChange("status", option.value)}
          />
        </div>

        {/* Withdraw Type */}
        <div className="col-md-2 mb-3">
          <label className="small text-muted font-weight-bold mb-1">Withdraw Type</label>
          <Select
            styles={filterSelectStyles}
            options={[
              { value: -1, label: "All" },
              { value: 0, label: "Bank" },
              { value: 1, label: "UPI" },
              { value: 2, label: "PayPal" },
            ]}
            value={
              [
                { value: -1, label: "All" },
                { value: 0, label: "Bank" },
                { value: 1, label: "UPI" },
                { value: 2, label: "PayPal" },
              ].find((o) => o.value === filters.withdrawType) || { value: -1, label: "All" }
            }
            onChange={(option) => handleFilterChange("withdrawType", option.value)}
          />
        </div>

        {/* Wallet Type */}
        <div className="col-md-2 mb-3">
          <label className="small text-muted font-weight-bold mb-1">Wallet Type</label>
          <Select
            styles={filterSelectStyles}
            options={[
              { value: -1, label: "All" },
              { value: 0, label: "Deposit" },
              { value: 1, label: "Withdraw" },
              { value: 2, label: "Bonus" },
            ]}
            value={
              [
                { value: -1, label: "All" },
                { value: 0, label: "Deposit" },
                { value: 1, label: "Withdraw" },
                { value: 2, label: "Bonus" },
              ].find((o) => o.value === filters.walletType) || { value: -1, label: "All" }
            }
            onChange={(option) => handleFilterChange("walletType", option.value)}
          />
        </div>

        {/* Search */}
        <div className="col-md-2 mb-3">
          <label className="small text-muted font-weight-bold mb-1">Search PlayerId</label>
          <div className="position-relative">
            <i 
              className="fas fa-search position-absolute text-muted" 
              style={{ left: "12px", top: "50%", transform: "translateY(-50%)", zIndex: 1 }}
            ></i>
            <input
              type="text"
              className="form-control premium-search-input"
              style={{ borderRadius: "10px", border: "1.5px solid #e2e8f0", padding: "8px 12px 8px 35px", fontSize: "0.95rem" }}
              placeholder="Search Playerid..."
              value={searchPhoneNo}
              onChange={(e) => {
                setPageNumber(1);
                setRecordLength(10);
                setSearchPhoneNo(e.target.value);
                handleFilterChange("playerId", e.target.value);
              }}
            />
          </div>
        </div>

        {/* Export Data Button */}
        <div className="col-md-2 mb-3 d-flex align-items-end">
          <button
            className="btn btn-success w-100 font-weight-bold"
            onClick={exportHistoryData}
            style={{ height: "42px", borderRadius: "10px", border: "none" }}
          >
            <i className="fas fa-file-excel mr-2"></i>
            Download
          </button>
        </div>

        {/* PDF Data Button */}
        {accessObject?.historyDownload == true && (
          <div className="col-md-2 mb-3 d-flex align-items-end">
            <button
              className="btn btn-primary w-100 font-weight-bold"
              onClick={() => {
                const input = document.getElementById("download-PDF");
                const opt = {
                  filename: "History-List.pdf",
                };
                html2pdf().set(opt).from(input).save().from().toPdf();
              }}
              style={{ height: "42px", borderRadius: "10px", border: "none" }}
            >
              <i className="fas fa-file-pdf mr-2"></i>
              PDF
            </button>
          </div>
        )}

      </div>
    </div>
  );

  return (
    <div>
      <div className="content p-3">
        <div className="card">
          {renderFilters()}
          <div
            class="card-body"
            id="download-PDF"
            style={{ overflow: "scroll" }}
          >
            <table class="table table-bordered table-striped" id="table-to-xls">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>Id</th>
                  <th style={{ textAlign: "center" }}>Date</th>
                  <th style={{ textAlign: "center" }}>Player Id</th>
                  <th style={{ textAlign: "center" }}>Email</th>
                  <th style={{ textAlign: "center" }}>Game</th>
                  <th style={{ textAlign: "center" }}>Transaction Type</th>
                  <th style={{ textAlign: "center" }}>Amount</th>
                  <th style={{ textAlign: "center" }}>Note</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                </tr>
              </thead>
              {accessObject?.getHistoryList == true ? (
                <>
                  <tbody>
                    {!loader ? (
                      <>
                        {dataRows?.length > 0 ? (
                          <>
                            {dataRows?.map((item, index) => (
                              <>
                                <tr key={index} style={{ textAlign: "center" }}>
                                  <td style={{ textAlign: "center" }}>
                                    {pageNumber === 1
                                      ? index + 1
                                      : index +
                                        1 +
                                        pageNumber * recordLength -
                                        recordLength}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {moment(item.createdAt).format(
                                      "DD-MM-YYYY"
                                    )}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {item.playerId ? item.playerId : "-"}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {item.email ? item.email : "-"}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {item.gameNo === 0
                                      ? "-"
                                      : item.gameNo === 1
                                      ? "Smash Block"
                                      : item.gameNo === 2
                                      ? "Tic Tac Toe"
                                      : item.gameNo === 3
                                      ? "Block Blast"
                                      : item.gameNo === 4
                                      ? "Word Book"
                                      : item.gameNo === 5
                                      ? "Number Puzzle 2248"
                                      : item.gameNo === 6
                                      ? "Logic Line"
                                      : item.gameNo === 7
                                      ? "Candy Cream"
                                      : item.gameNo === 8
                                      ? "8 ball Pool"
                                      : item.gameNo === 9
                                      ? "Chess Gamebit"
                                      : item.gameNo === 10
                                      ? "Ludo Lion"
                                      : item.gameNo === 11
                                      ? "Poker Pro"
                                      : item.gameNo === 12
                                      ? "Classy Carrom"
                                      : item.gameNo === 13
                                      ? "teenpatti"
                                      : "-"}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {item.transactionType === 0
                                      ? "Debit"
                                      : item.transactionType === 1
                                      ? "Credit"
                                      : "-"}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {Math.round(item.amount * 100) / 100}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {item.note === 0
                                      ? "Win"
                                      : item.note === 1
                                      ? "Bet"
                                      : item.note === 2
                                      ? "Add Cash"
                                      : item.note === 3
                                      ? "Withdraw"
                                      : item.note === 4
                                      ? "Reward"
                                      : item.note === 5
                                      ? "Refund"
                                      : item.note === 6
                                      ? "Test"
                                      : "-"}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {item.status === 0
                                      ? "Pending"
                                      : item.status === 1
                                      ? "Success"
                                      : item.status === 2
                                      ? "Failed"
                                      : "-"}
                                  </td>
                                </tr>
                              </>
                            ))}
                          </>
                        ) : (
                          <EmptyState colSpan={10} message="No History Found" subMessage="We couldn't find any history records matching your criteria." />
                        )}
                      </>
                    ) : (
                      ""
                    )}
                  </tbody>
                </>
              ) : (
                <>
                  <tr style={{ backgroundColor: "rgba(0,0,0,.05)" }}>
                    <td colSpan={10}>
                      <h5 className="text-center">Access Denied</h5>
                    </td>
                  </tr>
                </>
              )}
            </table>
            {accessObject?.getHistoryList == true && (
              <div className="mt-4">{loader && <Loader />}</div>
            )}
          </div>
          {accessObject?.getHistoryList == true && dataRows.length != 0 && (
            <Pagination
              updateShowPerpagevalue={updateShowPerpagevalue}
              showPrePage={showPrePage}
              onPaginationChange={onPaginationChange}
              total={dataRows.length}
              paginationTypeApi={true}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              recordLength={recordLength}
              setRecordLength={setRecordLength}
              totalLength={totalLength}
            />
          )}
        </div>
      </div>
    </div>
  );
}
